import { fromPairs, get, isArray, isEmpty, isEqual, isNil, isString, uniq, uniqWith } from "lodash";
import {
  extractRoleArrayLiteral,
  extractRoleLiterals,
  replaceRoleArrayLiteral,
  replaceRoleLiterals,
  ROLE_VALUED_PROPERTIES,
} from "../process-role-literals";
import type { nil } from "../type-utils";
import { mapExpressions } from "./process-model-copy-references";

/** A role list of a task or an auto-state-change mutation. */
interface RoleBearingMutation {
  targetRoles?: string[];
  responsibleRoles?: string[];
  visibleForRoles?: string[];
}

/** The role fields an area, phase or task can have (see `collectTaskRoleReferences` for the ones validation checks). */
export interface RoleBearingElement extends RoleBearingMutation {
  setupRoles?: string[];
  /** A list of roles, or the name of the task's role field to take them from. */
  recipientRoles?: string[] | string;
  autoStateChanges?: { mutations?: RoleBearingMutation[] }[];
  taskEmails?: (RoleBearingMutation & { recipientRoles?: string[] | string })[];
  signingRoleMapping?: { processRoleName?: string }[];
  organizationDocuments?: { organizationRole?: string }[];
  rules?: { propertyName?: string; expression?: string }[];
}

/** One place where a copied element uses a role key. */
export interface RoleUsage {
  roleKey: string;
  sourceModelId: string;
  field: string;
}

/** Role keys mapped to the key used instead. Keys not in the map stay. */
export type RoleKeyMap = ReadonlyMap<string, string>;

interface RoleField {
  field: string;
  roles: string[];
}

const LIST_FIELDS = ["visibleForRoles", "responsibleRoles", "targetRoles", "setupRoles", "recipientRoles"] as const;
const MUTATION_FIELDS = ["targetRoles", "responsibleRoles", "visibleForRoles"] as const;
const EMAIL_FIELDS = ["targetRoles", "responsibleRoles", "recipientRoles"] as const;

/**
 * Every role key an element lists in a role field: its own lists, auto-state-change mutations, e-mail recipients,
 * signing role mapping and organization documents. `Candidate` counts like any other key, since validation wants every
 * role the model refers to among its roles. A `recipientRoles` that names a role field instead of listing roles is
 * left out.
 */
export function collectRoleUsages(element: RoleBearingElement, sourceModelId: string): RoleUsage[] {
  const roleFields: RoleField[] = [
    ...LIST_FIELDS.map((field) => ({ field, roles: toRoleList(element[field]) })),
    ...(element.autoStateChanges ?? []).flatMap((change, changeIndex) =>
      (change?.mutations ?? []).flatMap((mutation, mutationIndex) =>
        MUTATION_FIELDS.map((field) => ({
          field: `autoStateChanges.${changeIndex}.mutations.${mutationIndex}.${field}`,
          roles: toRoleList(mutation?.[field]),
        }))
      )
    ),
    ...(element.taskEmails ?? []).flatMap((email, emailIndex) =>
      EMAIL_FIELDS.map((field) => ({ field: `taskEmails.${emailIndex}.${field}`, roles: toRoleList(email?.[field]) }))
    ),
    ...(element.signingRoleMapping ?? []).map((entry, index) => ({
      field: `signingRoleMapping.${index}.processRoleName`,
      roles: toRoleList([entry?.processRoleName]),
    })),
    ...(element.organizationDocuments ?? []).map((document, index) => ({
      field: `organizationDocuments.${index}.organizationRole`,
      roles: toRoleList([document?.organizationRole]),
    })),
  ];

  return roleFields.flatMap(({ field, roles }) => roles.map((roleKey) => ({ roleKey, sourceModelId, field })));
}

/**
 * The roles an element's expressions name literally: those passed to `getOrganizationId`, and those a rule writes into
 * a role field as a plain list. They are no role field, so they are reported apart from the role fields.
 */
export function collectRoleLiteralUsages(element: RoleBearingElement & object, sourceModelId: string): RoleUsage[] {
  const literalUsages: RoleUsage[] = [];
  mapExpressions(element, (expression, field) => {
    extractRoleLiterals(expression)
      .filter(isProcessRoleKey)
      .forEach((roleKey) => literalUsages.push({ roleKey, sourceModelId, field }));
    return expression;
  });

  const ruleUsages = (element.rules ?? []).flatMap((rule, index) =>
    ROLE_VALUED_PROPERTIES.has(rule?.propertyName)
      ? (extractRoleArrayLiteral(rule.expression) ?? [])
          .filter(isProcessRoleKey)
          .map((roleKey) => ({ roleKey, sourceModelId, field: `rules.${index}.expression` }))
      : []
  );
  return uniqueUsages([...literalUsages, ...ruleUsages]);
}

/**
 * A copy of the element with its role keys mapped: every role field takes the new key (once, should two keys map to
 * the same role), and expressions name the new key wherever they named the old one literally.
 */
export function applyRoleKeyMap<T extends RoleBearingElement & object>(element: T, roleKeyMap: RoleKeyMap): T {
  if (roleKeyMap.size === 0) {
    return element;
  }

  const mapRole = (role: string): string => roleKeyMap.get(role?.trim()) ?? role;
  const mapRoles = (roles: string[]): string[] => uniq(roles.map(mapRole));
  const mapRoleFields = <E extends object>(target: E, fields: readonly string[]): E => {
    const mappedFields = fields
      .filter((field) => isArray(get(target, field)))
      .map((field): [string, string[]] => [field, mapRoles(get(target, field))]);
    return { ...target, ...fromPairs(mappedFields) };
  };

  const withLists = mapRoleFields(element, LIST_FIELDS);
  const withNested: T = {
    ...withLists,
    ...(isNil(element.autoStateChanges)
      ? {}
      : {
          autoStateChanges: element.autoStateChanges.map((change) =>
            isNil(change?.mutations)
              ? change
              : { ...change, mutations: change.mutations.map((mutation) => mapRoleFields(mutation, MUTATION_FIELDS)) }
          ),
        }),
    ...(isNil(element.taskEmails)
      ? {}
      : { taskEmails: element.taskEmails.map((email) => mapRoleFields(email, EMAIL_FIELDS)) }),
    ...(isNil(element.signingRoleMapping)
      ? {}
      : {
          signingRoleMapping: element.signingRoleMapping.map((entry) => ({
            ...entry,
            processRoleName: mapRole(entry.processRoleName),
          })),
        }),
    ...(isNil(element.organizationDocuments)
      ? {}
      : {
          organizationDocuments: element.organizationDocuments.map((document) =>
            mapOrganizationRole(document, mapRole)
          ),
        }),
  };

  return renameRoleLiterals(withNested, roleKeyMap);
}

/** The role keys of a map that point to another key: the ones expressions have to be rewritten for. */
export function getRenamedRoleKeys(roleKeyMap: RoleKeyMap): Map<string, string> {
  return new Map(Array.from(roleKeyMap.entries()).filter(([key, newKey]) => newKey !== key));
}

function renameRoleLiterals<T extends RoleBearingElement & object>(element: T, roleKeyMap: RoleKeyMap): T {
  const renamedRoleKeys = getRenamedRoleKeys(roleKeyMap);
  if (renamedRoleKeys.size === 0) {
    return element;
  }

  const withExpressions = mapExpressions(element, (expression) => replaceRoleLiterals(expression, renamedRoleKeys));
  if (isNil(withExpressions.rules)) {
    return withExpressions;
  }

  return {
    ...withExpressions,
    rules: withExpressions.rules.map((rule) =>
      ROLE_VALUED_PROPERTIES.has(rule?.propertyName) && isString(rule.expression)
        ? { ...rule, expression: replaceRoleArrayLiteral(rule.expression, renamedRoleKeys) }
        : rule
    ),
  };
}

function mapOrganizationRole<D extends { organizationRole?: string }>(
  document: D,
  mapRole: (role: string) => string
): D {
  // Without a role the document is taken from the process owner, see OrganizationDocumentModel
  if (isNil(document?.organizationRole)) {
    return document;
  }

  return { ...document, organizationRole: mapRole(document.organizationRole) };
}

function toRoleList(value: string[] | string | nil): string[] {
  if (!isArray(value)) {
    return [];
  }

  return uniq(value.filter((role) => isString(role)).map((role) => role.trim())).filter(isProcessRoleKey);
}

function isProcessRoleKey(roleKey: string): boolean {
  return !isEmpty(roleKey);
}

function uniqueUsages(usages: RoleUsage[]): RoleUsage[] {
  return uniqWith(usages, isEqual);
}
